import s from './style.module.scss';
import { useCallback, useEffect, useState } from 'react';
import useStatusBarHeight from '@/hooks/useStatusBarHeight';
import cx from 'classnames';
import { useToast } from '@/stores/ToastContext';
import { getSubscriptions, unsubscribe } from '@/pages/api/subscription_manage';
import { TimePeriods, SubscriptionCategoryMap } from './const';
import { sendPoint } from '@/util/common';

const Options = ['Too expensive', 'It was just a trial', 'Paid features are useless', 'I prefer other apps', 'Other'];
export default function Questionnaire() {
  const showToast = useToast();
  const statusBarHeight = useStatusBarHeight();
  const [subscriptions, setSubscriptions] = useState([]);
  const [curSubscription, setCurSubscription] = useState(null);
  const [curStage, setCurStage] = useState(1);
  const [curOption, setCurOption] = useState(null);
  const [shuffledOptions] = useState(() => [...Options].sort(() => Math.random() - 0.5));

  const renderQuestion = () => {
    const onUnsubscribe = async () => {
      const { serialId, platform } = curSubscription;
      const result = await unsubscribe(serialId, platform);
      if (result.meta?.code === 200) {
        sendPoint({ event_name: 'duet_web_unsubscribe_success', option: curOption });
        setCurStage(1);
      } else {
        showToast('Current network error. Please try again in 2 minutes');
      }
    };
    return (
      <div className={s.questionWrapper}>
        <div className={s.question}>
          Please tell us why you cancel, so that we can provide better services next time.
        </div>
        {shuffledOptions.map((option, index) => (
          <div key={index} className={s.option}>
            <div>{option}</div>
            <div
              className={cx(s.radio, { [s.checked]: curStage === 3 && curOption === option })}
              onClick={() => {
                setCurOption(option);
              }}
            />
          </div>
        ))}
        <div className={cx(s.submitButton, { [s.active]: curOption })} onClick={onUnsubscribe}>
          Submit
        </div>
      </div>
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      const subscriptions = await getSubscriptions();
      setSubscriptions(subscriptions?.data?.subscriptions || []);
    };
    if (curStage === 1) {
      fetchData();
    }
  }, [curStage]);

  const onBack = useCallback(() => {
    if (curStage > 1) {
      setCurStage(1);
    } else {
      history.back();
    }
  }, [curStage]);

  const renderDesc = useCallback((subscription) => {
    const date = new Date(subscription.expire);
    const minutes = date.getMinutes() < 10 ? `0${date.getMinutes()}` : date.getMinutes();
    const hours = date.getHours() < 10 ? `0${date.getHours()}` : date.getHours();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    if (!subscription.autoRenew) {
      return `Expires: ${year}-${month}-${date.getDate()} ${hours}:${minutes}`;
    }
    if (subscription.autoRenew && subscription.expire > Date.now()) {
      return `Renewal date: ${year}-${month}-${date.getDate()} ${hours}:${minutes}`;
    }
    return 'Subscription has expired';
  }, []);

  const renderSubscription = useCallback((subscription) => {
    const { merchandise } = subscription;
    const period = TimePeriods.find((item) => item.value === merchandise.quantity && item.unit === merchandise.unit);
    return (
      <div
        className={s.subscription}
        key={subscription.id}
        onClick={() => {
          setCurSubscription(subscription);
          setCurStage(2);
        }}
      >
        <div className={cx(s.nameLine, { [s.supreme]: merchandise.category === 'supreme' })}>
          <div className={s.vipIcon} />
          <div className={s.subscriptionName}>
            {`${SubscriptionCategoryMap[merchandise.category]}  ${period.valueTitle || period.value}  ${period.title}`}
          </div>
        </div>
        <div className={cx(s.statusLine, { [s.canceled]: !subscription.autoRenew })}>
          <div>{subscription.autoRenew ? 'Subscribed' : 'Canceled'}</div>
          <div className={s.detailIcon} />
        </div>
        <div className={s.desc}>{renderDesc(subscription)}</div>
        <div className={s.divider} />
      </div>
    );
  }, []);

  const renderDetail = useCallback(() => {
    const subscription = curSubscription;
    const { merchandise } = subscription;
    const period = TimePeriods.find((item) => item.value === merchandise.quantity && item.unit === merchandise.unit);
    return (
      <div className={s.subscriptionList} key={subscription.id}>
        <div className={cx(s.nameLine, { [s.supreme]: merchandise.category === 'supreme' })}>
          <div className={s.vipIcon} />
          <div className={s.subscriptionName}>
            {`${SubscriptionCategoryMap[merchandise.category]}  ${period.valueTitle || period.value}  ${period.title}`}
          </div>
        </div>
        <div className={cx(s.statusLine, { [s.canceled]: !subscription.autoRenew })}>
          {subscription.autoRenew ? 'Subscribed' : 'Canceled'}
        </div>
        <div className={s.desc}>{renderDesc(subscription)}</div>
        {curSubscription.autoRenew && (
          <div className={s.cancelButton} onClick={() => setCurStage(3)}>
            Unsubscribe
          </div>
        )}
      </div>
    );
  }, [curSubscription]);

  return (
    <div className={s.root}>
      <div onClick={onBack} className={s.navBar} style={{ top: `${statusBarHeight}rem` }}>
        <div className={s.backArrow} />
        {curStage < 3 ? 'Subscription Management' : 'Why Do You Unsubscribe?'}
      </div>
      {curStage === 1 && <div className={s.subscriptionList}>{subscriptions.map(renderSubscription)}</div>}
      {curStage === 2 && renderDetail()}
      {curStage === 3 && renderQuestion()}
      <div className={s.bg} />
    </div>
  );
}
